import { Doc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_ARR } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  IExperienceDenormalizedV2,
  IExperienceMessageDenormalized,
  ITaskDenormalized,
  PARTICIPANT_ACTOR_KEY,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import { IOrganizationDenormalized } from '@dadohr/shared/domainTypes/org';
import { IUserDenormalized } from '@dadohr/shared/domainTypes/user';
import styled from '@emotion/styled';
import { Button, IconButton, Typography } from '@material-ui/core';
import { compact, truncate } from 'lodash';
import { CircleArrowLeft, CircleArrowRight } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useUnsafeTranslation } from '../../../../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical,
  FlexItem
} from '../../../../../../../layout/Flex';
import { toFullName } from '../../../../../../../services/user';
import {
  SubTaskContext,
  TaskBody
} from '../../../../../../features/Experience/components/Task';
import { getSubTaskContext } from '../../../../../../features/Experience/service';
import { useProductAnalytics } from '../../../../../../features/Experience/services/productAnalytics';
import { WithBreakpoints } from '../../../components/Breakpoints';
import { EmployeeAppCard } from '../../../components/EmployeeAppCard';
import { MessageCard } from '../../../components/Messages';
import { DueOrCompletionDate } from '../../../components/Tasks';
import { TaskStatusChipV2 } from '../../../components/TaskStatusChipV2';
import { getIndividualInstanceUrl } from '../../../services/routes';

const Container = styled('div')((p) => ({
  width: '100%'
}));

export const InboxTaskDetails = ({
  instance,
  task,
  actorAccessToken,
  instanceAccessToken,
  returnTo,
  accompanyingMessage,
  nextButtons = EMPTY_ARR,
  noBackButton = false,
  showTemplateTitle = false,
  additionalLinkInHeader = null
}: {
  instance: Doc<IExperienceDenormalizedV2>;
  task: ITaskDenormalized;
  actorAccessToken: string;
  instanceAccessToken: string;
  returnTo?: string;
  noBackButton?: boolean;
  showTemplateTitle?: boolean;
  accompanyingMessage: IExperienceMessageDenormalized | undefined | null;
  nextButtons?: {
    label: string;
    url: string;
    variant?: 'outlined' | 'contained';
  }[];
  additionalLinkInHeader?: {
    label: string;
    url: string;
  } | null;
}) => {
  const { trackEvent } = useProductAnalytics(instanceAccessToken);
  const { t } = useUnsafeTranslation();
  const context = useMemo<{
    user: IUserDenormalized;
    userId: string;
    org: IOrganizationDenormalized;
    subTaskContext: SubTaskContext;
  }>(() => {
    return {
      user: instance.data.user,
      userId: instance.data.userId || '',
      org: instance.data.org,
      subTaskContext: getSubTaskContext(task.id, instance, trackEvent)
    };
  }, [instance, task, trackEvent]);

  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // The parent element is the scroll container. When we move between tasks, go back to the top
    ref.current?.parentElement?.scrollTo(0, 0);
  }, [task.id]);

  const backUrl =
    returnTo || getIndividualInstanceUrl(actorAccessToken, instanceAccessToken);

  const isSupportingActor = task.actor !== PARTICIPANT_ACTOR_KEY;
  const showSubTitle = showTemplateTitle || isSupportingActor;

  const title = (
    <FlexItem flex={3}>
      <FlexContainerVertical spacing={0.5}>
        <Typography variant="h4">{task.title}</Typography>
        {showSubTitle && (
          <FlexContainer spacing={3} wrap="wrap" rowSpacing={0.5}>
            <Typography variant="h5" color="textSecondary">
              {compact([
                showTemplateTitle && instance.data.title,
                isSupportingActor && toFullName(instance.data.participant)
              ]).join(' • ')}
            </Typography>
            {additionalLinkInHeader && (
              <Link to={additionalLinkInHeader.url}>
                <Typography variant="body2" color="primary">
                  {additionalLinkInHeader.label} <CircleArrowRight size={12} />
                </Typography>
              </Link>
            )}
          </FlexContainer>
        )}
      </FlexContainerVertical>
    </FlexItem>
  );

  return (
    <Container ref={ref}>
      <FlexContainerVertical fullWidth spacing={3}>
        <FlexContainerVertical fullWidth spacing={2}>
          {!noBackButton && (
            <Link
              to={backUrl}
              onClick={
                () => undefined
                // TODO
                // trackEvent({
                //   eventType: PAEventType.EMPLOYEE_EXPERIENCE_TASK,
                //   name: 'Back Link Clicked',
                //   Explanation:
                //     'A user clicked the back link at the top of the single task page',
                //   'Task ID': task.id,
                //   'Back URL': backUrl
                // })
              }
            >
              <IconButton color="primary">
                <CircleArrowLeft size={24} />
              </IconButton>
            </Link>
          )}
          <WithBreakpoints
            lg={
              <FlexContainer fullWidth justifyContent={'space-between'}>
                {title}
                <FlexItem flex={1}>
                  <FlexContainer fullWidth justifyContent={'flex-end'}>
                    <TaskStatusChipV2 status={task.status} />
                    <DueOrCompletionDate task={task} />
                  </FlexContainer>
                </FlexItem>
              </FlexContainer>
            }
            md={
              <FlexContainer fullWidth justifyContent={'space-between'}>
                {title}
                <FlexItem flex={1}>
                  <FlexContainer fullWidth justifyContent={'flex-end'}>
                    <TaskStatusChipV2 status={task.status} />
                    <DueOrCompletionDate task={task} />
                  </FlexContainer>
                </FlexItem>
              </FlexContainer>
            }
            sm={
              <FlexContainer fullWidth justifyContent={'space-between'}>
                {title}
                <FlexItem flex={1}>
                  <FlexContainer fullWidth justifyContent={'flex-end'}>
                    <TaskStatusChipV2 status={task.status} />
                    <DueOrCompletionDate task={task} />
                  </FlexContainer>
                </FlexItem>
              </FlexContainer>
            }
            xs={
              <FlexContainerVertical fullWidth spacing={1}>
                {title}
                <FlexContainer>
                  <TaskStatusChipV2 status={task.status} />
                  <DueOrCompletionDate task={task} />
                </FlexContainer>
              </FlexContainerVertical>
            }
          />
          {accompanyingMessage && (
            <MessageCard
              d={accompanyingMessage}
              instanceAccessToken={instanceAccessToken}
            />
          )}
          <EmployeeAppCard>
            <TaskBody
              taskId={task.id}
              subtasks={task.blocks}
              context={context}
              noContainerMargins
            />
          </EmployeeAppCard>
        </FlexContainerVertical>
        {nextButtons.length > 0 && (
          <FlexContainer>
            {nextButtons.map((nextButton, i) => (
              <Link to={nextButton.url} key={i}>
                <Button
                  color="primary"
                  variant={
                    nextButton.variant ||
                    (task.status === TaskStatus.COMPLETE
                      ? 'contained'
                      : 'outlined')
                  }
                >
                  {truncate(nextButton.label, {
                    length: Math.round(100 / nextButtons.length)
                  })}
                </Button>
              </Link>
            ))}
          </FlexContainer>
        )}
      </FlexContainerVertical>
    </Container>
  );
};
